import type { TExample } from '@atlas/uicorn/preview/modules';
import * as React from 'react';
import styles from './Spinner.module.css';
import cn from 'classnames';

const Spinner = React.memo(function Spinner(props: TSpinnerProps): React.ReactElement {
    const { horizontalFit, verticalFit, small, className, ...elementProps } = props;

    const containerClassName = cn(styles.spinnerContainer, {
        [styles.horizontalFit]: horizontalFit,
        [styles.verticalFit]: verticalFit,
    });

    const spinnerClassName = cn(styles.spinner, className, {
        [styles.small]: small,
    });

    if (horizontalFit || verticalFit) {
        return (
            <div className={containerClassName}>
                <svg {...elementProps} className={spinnerClassName} version="1.1" xmlns="http://www.w3.org/2000/svg">
                    <circle />
                </svg>
            </div>
        );
    }

    return (
        <svg {...elementProps} className={spinnerClassName} version="1.1" xmlns="http://www.w3.org/2000/svg">
            <circle />
        </svg>
    );
});

export default Spinner;

// #region Types

export type TSpinnerProps = Omit<React.SVGAttributes<SVGElement>, 'children'> & {
    small?: boolean;
    horizontalFit?: boolean;
    verticalFit?: boolean;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render({ small }) {
        return <Spinner small={small} />;
    },
    controls: {
        small: { type: 'boolean', initial: false },
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System----2.0?node-id=1462:5918&mode=dev',
};

export const AlignedSpinnerExample: TExample = {
    containerStyle: {
        width: '40vh',
        height: '40vh',
        border: '1px dashed green',
        padding: 0,
        display: 'block',
    },
    render({ horizontalFit, verticalFit }) {
        return <Spinner verticalFit={verticalFit} horizontalFit={horizontalFit} />;
    },
    controls: {
        verticalFit: { type: 'boolean', initial: true },
        horizontalFit: { type: 'boolean', initial: true },
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System----2.0?node-id=1462:5918&mode=dev',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Spinner', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
